import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AgreementItem from '../components/AgreementItem'
import ContactSyncSkipModal from '../components/ContactSyncSkipModal'
import SubmitButton from '../components/SubmitButton'
import TermsModal from '../components/TermsModal'
import { toastUtil } from '../utils/toast'
import { isApp } from '../utils/platform'
import { useAuthStore } from '../stores/authStore'
import { userApi } from '../api/user'

const ContactSync: React.FC = () => {
  const navigate = useNavigate()
  // 친구 초대·팔로워 목록에서 왔으면 연락처 친구 화면에도 알려서, 끝나면 그 화면으로 돌아가게 한다.
  // replace 로 넘긴다 — 연락처 친구 화면에서 뒤로 가면 이 동의 화면이 아니라 들어온 화면이 나온다.
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const goFriends = useCallback(
    () =>
      from
        ? navigate(`/contact-friends?from=${encodeURIComponent(from)}`, { replace: true })
        : navigate('/contact-friends'),
    [from, navigate]
  )
  const { setContactSyncDone } = useAuthStore()
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [termsModalOpen, setTermsModalOpen] = useState(false)
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false)

  /*
   * 허용됐는데 목록이 안 오는 경우를 끊는 타이머 (InviteFriends 와 같은 이유).
   * 앱은 전화번호 있는 연락처가 0개면 '허용됨'만 보내고 목록은 안 보낸다 —
   * 아이폰 '일부 연락처만 허용'에서 아무도 안 고른 경우. 7초 지나면 없는 것으로 본다.
   */
  const waitRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Flutter 브리지 콜백 등록
  useEffect(() => {
    if (isApp() && (window as any).FlutterBridge) {
      const flutterBridge = (window as any).FlutterBridge

      // 연락처 권한 결과 콜백 등록
      flutterBridge.onContactsPermissionResult = async (data: any) => {
        console.debug('연락처 권한 결과:', data)
        if (data && data.granted) {
          // 권한이 허용된 경우 연락처 데이터는 onContactsData에서 처리
          if (waitRef.current) clearTimeout(waitRef.current)
          waitRef.current = setTimeout(async () => {
            waitRef.current = null
            setIsLoading(false)
            toastUtil.error('설정에서 연락처 접근을 허용해주세요')
            setContactSyncDone()
            try {
              await userApi.updateContactConsent(true)
            } catch (error) {
              console.error('연락처 연동 동의 저장 실패:', error)
            }
            goFriends()
          }, 7000)
        } else {
          setIsLoading(false)
          // 앱이 보내는 문구(data.message) 대신 안내 문구를 하나로 맞춘다(2026-11-04)
          toastUtil.error('설정에서 연락처 접근을 허용해주세요')
          setContactSyncDone()
          try {
            await userApi.updateContactConsent(true)
          } catch (error) {
            console.error('연락처 연동 동의 저장 실패:', error)
          }
          goFriends()
        }
      }

      // 연락처 데이터 콜백 등록 (첫 50개만 수신)
      flutterBridge.onContactsData = async (data: any) => {
        if (waitRef.current) {
          clearTimeout(waitRef.current)
          waitRef.current = null
        }
        if (data && data.contacts && Array.isArray(data.contacts)) {
          setIsLoading(false)
          localStorage.setItem(
            'contacts_data',
            JSON.stringify({
              contacts: data.contacts,
              offset: data.offset ?? 0,
              total: data.total ?? data.contacts.length,
            })
          )
          setContactSyncDone()
          try {
            await userApi.updateContactConsent(true)
          } catch (error) {
            console.error('연락처 연동 동의 저장 실패:', error)
          }
          goFriends()
        } else {
          setIsLoading(false)
          console.error('ContactSync: 연락처 데이터 형식 오류:', data)
        }
      }

      // 클린업
      return () => {
        if (waitRef.current) clearTimeout(waitRef.current)
        if (flutterBridge.onContactsPermissionResult) {
          delete flutterBridge.onContactsPermissionResult
        }
        if (flutterBridge.onContactsData) {
          delete flutterBridge.onContactsData
        }
      }
    }
  }, [navigate, setContactSyncDone, goFriends])

  const handleSyncContacts = () => {
    if (!agreeTerms) {
      return
    }

    // 앱 환경이면 Flutter 브리지 사용
    if (isApp() && (window as any).FlutterBridge) {
      setIsLoading(true)
      ;(window as any).FlutterBridge.requestContactsPermission()
    } else {
      // 웹 환경에서는 연락처 접근이 제한적
      toastUtil.error('연락처 연동은 앱에서만 가능합니다')
    }
  }

  const handleConfirmSkipContactSync = async () => {
    setSkipConfirmOpen(false)
    setContactSyncDone()
    try {
      await userApi.updateContactConsent(false)
    } catch (error) {
      console.error('연락처 연동 건너뛰기 동의 저장 실패:', error)
    }
    if (from) {
      navigate(-1)
      return
    }
    navigate('/contact-friends')
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="app-container h-full">
        <div className="w-full h-full flex flex-col px-20px pt-52px pb-48px">
          <div className="w-full flex flex-col mb-12px">
            <span className="font-pretendard font-regular text-24px leading-125% text-brown-80%-main text-left">
              내 취향으로 채워질&nbsp;
              <strong className="font-pretendard font-bold">TASTEE,</strong>
              <br />
              친구들과 함께 시작해 보세요
            </span>
          </div>

          <div className="w-full mb-24px font-regular text-16px text-brown-80%-main text-left">
            연락처는 오직 친구 추천 목적으로만 사용됩니다
          </div>

          <div className="flex-1 flex items-center justify-center mb-24px">
            <img
              src="/images/phone-icon.png"
              alt="연락처"
              className="w-96px h-91px object-contain"
            />
          </div>

          <div className="w-full mb-24px">
            <AgreementItem
              checked={agreeTerms}
              onChange={(checked) => setAgreeTerms(checked)}
              label="필수"
              text="TASTEE 이용 약관"
              onViewClick={() => {
                setTermsModalOpen(true)
              }}
            />
          </div>

          <div className="w-full mb-24px">
            <SubmitButton
              text="연락처 연동"
              disabled={!agreeTerms}
              loading={isLoading}
              onSubmit={handleSyncContacts}
            />
          </div>

          <button
            type="button"
            onClick={() => setSkipConfirmOpen(true)}
            className="w-full font-700 text-16px text-brown-80%-main text-center"
          >
            건너뛰기
          </button>
        </div>
      </div>

      <TermsModal
        isOpen={termsModalOpen}
        type="terms"
        onClose={() => setTermsModalOpen(false)}
      />

      <ContactSyncSkipModal
        isOpen={skipConfirmOpen}
        onCancel={() => setSkipConfirmOpen(false)}
        onConfirmSkip={handleConfirmSkipContactSync}
      />
    </div>
  )
}

export default ContactSync
